import { createFileRoute, Link } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api, ApiError } from '../lib/api';
import { requireAccess } from '../lib/guards';
import { Button } from '../components/ui/Button';
import { ErrorBanner } from '../components/ui/ErrorBanner';
import { Input } from '../components/ui/Input';
import { PageHeader } from '../components/ui/PageHeader';
import { type BvStatus } from '../lib/api-enums';
import { BV_STATUS_LABELS, BV_STATUS_BADGE } from '../lib/api-labels';

interface Bv {
  id: string;
  bvNummer: string | null;
  entId: string | null;
  kundeId: string;
  objektId: string;
  status: BvStatus;
  laufzeitVon: string;
  gekuendigtAm: string | null;
  beendetZum: string | null;
  pauseVon: string | null;
  pauseBis: string | null;
  zahlungsart: string;
  zahlungszielTage: number;
  ustSatz: string;
  monatsbetragNetto: string;
  quartalsbetragNetto: string;
  skontoProzent: string | null;
  skontoTage: number | null;
  unterschriebenesPdfStorageKey: string | null;
  unterschriebenAm: string | null;
}

interface Kunde {
  id: string;
  firmenname: string;
  kundennummer: string;
}

interface Objekt {
  id: string;
  bezeichnung: string;
}

export const Route = createFileRoute('/vertraege/bv/$id')({
  beforeLoad: () => requireAccess('/api/v1/vertraege/bv'),
  component: BvDetailPage,
});

function BvDetailPage() {
  const { id } = Route.useParams();
  const qc = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [pausierenOpen, setPausierenOpen] = useState(false);

  const bvQ = useQuery({
    queryKey: ['vertraege', 'bv', id],
    queryFn: () => api<{ item: Bv }>(`/api/v1/vertraege/bv/${id}`),
  });
  const kundenQ = useQuery({
    queryKey: ['kunden', 'lookup'],
    queryFn: () => api<{ items: Kunde[] }>('/api/v1/kunden'),
  });
  const objekteQ = useQuery({
    queryKey: ['objekte', 'lookup'],
    queryFn: () => api<{ items: Objekt[] }>('/api/v1/objekte'),
  });

  const entpausierenMut = useMutation({
    mutationFn: () => api(`/api/v1/vertraege/bv/${id}/entpausieren`, { method: 'POST', body: {} }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['vertraege', 'bv', id] });
      void qc.invalidateQueries({ queryKey: ['vertraege', 'bvs'] });
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : String(e)),
  });

  const kuendigenMut = useMutation({
    mutationFn: (body: { gekuendigtAm: string; beendetZum: string }) =>
      api(`/api/v1/vertraege/bv/${id}/kuendigen`, { method: 'POST', body }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['vertraege', 'bv', id] });
      void qc.invalidateQueries({ queryKey: ['vertraege', 'bvs'] });
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : String(e)),
  });

  const pausierenMut = useMutation({
    mutationFn: (body: { pauseVon: string; pauseBis: string; grund?: string }) =>
      api(`/api/v1/vertraege/bv/${id}/pausieren`, { method: 'POST', body }),
    onSuccess: () => {
      setPausierenOpen(false);
      void qc.invalidateQueries({ queryKey: ['vertraege', 'bv', id] });
      void qc.invalidateQueries({ queryKey: ['vertraege', 'bvs'] });
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : String(e)),
  });

  if (bvQ.isLoading) return <div className="p-4 text-sm text-slate-500">Lade…</div>;
  if (!bvQ.data) return <div className="p-4 text-sm text-slate-500">BV nicht gefunden.</div>;

  const bv = bvQ.data.item;
  const kunde = (kundenQ.data?.items ?? []).find((k) => k.id === bv.kundeId);
  const objekt = (objekteQ.data?.items ?? []).find((o) => o.id === bv.objektId);

  return (
    <div className="space-y-4">
      <PageHeader
        title={bv.bvNummer ?? `BV ${bv.id.slice(0, 8)}`}
        description={`${kunde?.firmenname ?? '—'} / ${objekt?.bezeichnung ?? '—'} · seit ${bv.laufzeitVon}`}
        actions={
          <Link to="/vertraege" className="text-sm text-slate-600 hover:text-slate-800">
            ← zurück zur Liste
          </Link>
        }
      />

      {error ? <ErrorBanner error={error} /> : null}

      <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
        <div className="flex flex-wrap items-center gap-4 text-sm">
          <span className="font-medium text-slate-700">Status:</span>
          <span
            className={`inline-flex rounded-md border px-2 py-0.5 text-xs font-medium ${BV_STATUS_BADGE[bv.status]}`}
          >
            {BV_STATUS_LABELS[bv.status]}
          </span>
          {bv.pauseVon && bv.pauseBis ? (
            <span className="text-xs text-blue-700">
              Pause: {bv.pauseVon} → {bv.pauseBis}
            </span>
          ) : null}
          {bv.gekuendigtAm ? (
            <span className="text-xs text-orange-700">
              gekündigt {bv.gekuendigtAm}, beendet zum {bv.beendetZum ?? '—'}
            </span>
          ) : null}
        </div>

        <div className="mt-4 flex flex-wrap gap-2">
          {bv.status === 'aktiv' ? (
            <>
              <Button onClick={() => setPausierenOpen(true)} variant="ghost">
                Pausieren
              </Button>
              <Button
                variant="ghost"
                onClick={() => {
                  const today = new Date().toISOString().slice(0, 10);
                  const beendet = window.prompt(
                    'Beenden zum (YYYY-MM-DD, üblich Quartals-Ende):',
                    today,
                  );
                  if (beendet) kuendigenMut.mutate({ gekuendigtAm: today, beendetZum: beendet });
                }}
              >
                Kündigen
              </Button>
            </>
          ) : null}
          {bv.status === 'pause' ? (
            <Button onClick={() => entpausierenMut.mutate()} disabled={entpausierenMut.isPending}>
              Pause beenden
            </Button>
          ) : null}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
          <h2 className="mb-3 text-sm font-semibold text-slate-700">Konditionen</h2>
          <dl className="space-y-2 text-sm">
            <div className="flex justify-between">
              <dt className="text-slate-500">Monatsbetrag netto</dt>
              <dd className="font-mono text-slate-700">{bv.monatsbetragNetto} €</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-slate-500">Quartalsbetrag netto</dt>
              <dd className="font-mono text-slate-700">{bv.quartalsbetragNetto} €</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-slate-500">USt-Satz</dt>
              <dd className="font-mono text-slate-700">{bv.ustSatz} %</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-slate-500">Zahlungsziel</dt>
              <dd className="font-mono text-slate-700">{bv.zahlungszielTage} Tage</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-slate-500">Zahlungsart</dt>
              <dd className="font-mono text-slate-700">{bv.zahlungsart}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-slate-500">Skonto</dt>
              <dd className="font-mono text-slate-700">
                {bv.skontoProzent && bv.skontoTage
                  ? `${bv.skontoProzent}% / ${bv.skontoTage}d`
                  : '—'}
              </dd>
            </div>
          </dl>
        </div>

        <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
          <h2 className="mb-3 text-sm font-semibold text-slate-700">Vertragsdokument</h2>
          <dl className="space-y-2 text-sm">
            <div className="flex justify-between">
              <dt className="text-slate-500">Unterschrieben am</dt>
              <dd className="font-mono text-slate-700">{bv.unterschriebenAm ?? '—'}</dd>
            </div>
            <div>
              <dt className="text-slate-500">Storage-Key</dt>
              <dd className="mt-1 break-all font-mono text-xs text-slate-600">
                {bv.unterschriebenesPdfStorageKey ?? '—'}
              </dd>
            </div>
            {bv.entId ? (
              <div>
                <dt className="text-slate-500">Entwurf-Quelle</dt>
                <dd className="mt-1">
                  <Link
                    to="/vertraege/entwurf/$id"
                    params={{ id: bv.entId }}
                    className="text-brand-600 hover:underline"
                  >
                    Zum Entwurf →
                  </Link>
                </dd>
              </div>
            ) : null}
          </dl>
        </div>
      </div>

      {pausierenOpen ? (
        <PausierenModal
          onClose={() => setPausierenOpen(false)}
          onSubmit={(body) => pausierenMut.mutate(body)}
          submitting={pausierenMut.isPending}
        />
      ) : null}
    </div>
  );
}

function PausierenModal({
  onClose,
  onSubmit,
  submitting,
}: {
  onClose: () => void;
  onSubmit: (body: { pauseVon: string; pauseBis: string; grund?: string }) => void;
  submitting: boolean;
}) {
  const today = new Date().toISOString().slice(0, 10);
  const [pauseVon, setPauseVon] = useState(today);
  const [pauseBis, setPauseBis] = useState('');
  const [grund, setGrund] = useState('');

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4">
      <div className="w-full max-w-md rounded-lg bg-white p-5 shadow-xl">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-base font-semibold text-slate-900">BV pausieren</h2>
          <button
            onClick={onClose}
            className="rounded text-sm text-slate-400 hover:text-slate-600"
            aria-label="Schließen"
          >
            ✕
          </button>
        </div>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-slate-700">Pause von *</label>
              <Input type="date" value={pauseVon} onChange={(e) => setPauseVon(e.target.value)} />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-700">Pause bis *</label>
              <Input type="date" value={pauseBis} onChange={(e) => setPauseBis(e.target.value)} />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-700">Grund (optional)</label>
            <Input
              value={grund}
              onChange={(e) => setGrund(e.target.value)}
              placeholder="z.B. Sanierung Q3"
            />
          </div>
        </div>
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>
            Abbrechen
          </Button>
          <Button
            onClick={() => {
              if (!pauseVon || !pauseBis) return;
              onSubmit(grund ? { pauseVon, pauseBis, grund } : { pauseVon, pauseBis });
            }}
            disabled={submitting || !pauseVon || !pauseBis}
          >
            {submitting ? 'Pausiere…' : 'Pausieren'}
          </Button>
        </div>
      </div>
    </div>
  );
}
